import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  MapPin, 
  Users, 
  Calendar, 
  Wifi, 
  Copy,
  Home,
  Key,
} from 'lucide-react';
import { usePublicBookingByShortId } from '@/hooks/public/usePublicBookingByShortId';
import { useProperty } from '@/hooks/useProperties';
import { usePropertyRules } from '@/hooks/usePropertyRules';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const BookingDetail = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { data: booking, isLoading: bookingLoading, error: bookingError } = usePublicBookingByShortId(bookingId || '');
  const { data: property, isLoading: propertyLoading } = useProperty(booking?.property_id || '');
  const { data: propertyRules = [], isLoading: rulesLoading } = usePropertyRules(booking?.property_id || '');
  
  const [selectedPlaceCategory, setSelectedPlaceCategory] = useState<string>("Todos");

  // Real data from property
  const entryInstructions = {
    description: property?.entryDescription || '',
    code: property?.securityCode || '',
    videoUrl: property?.entryVideoUrl || ''
  };

  const wifiCredentials = {
    network: property?.wifiNetwork || '',
    password: property?.wifiPassword || ''
  };

  const mockTopPlaces = [
    {
      name: "Fresh Local",
      category: "Restaurante",
      image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop"
    },
    {
      name: "La Terraza Bar",
      category: "Bar",
      image: "https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=400&h=300&fit=crop"
    },
    {
      name: "Playa La Audiencia",
      category: "Playa",
      image: "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=400&h=300&fit=crop"
    },
    {
      name: "Kayak Manzanillo",
      category: "Aventura",
      image: "https://images.unsplash.com/photo-1472745942893-4b9f730c7668?w=400&h=300&fit=crop"
    },
    {
      name: "Mariscos El Dorado",
      category: "Restaurante",
      image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop"
    },
    {
      name: "Sunset Lounge",
      category: "Bar",
      image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=300&fit=crop"
    },
    {
      name: "Playa Santiago",
      category: "Playa",
      image: "https://images.unsplash.com/photo-1473116763249-2faaef81ccda?w=400&h=300&fit=crop"
    },
    {
      name: "Buceo Manzanillo",
      category: "Aventura",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop"
    }
  ];

  const placeCategories = ["Todos", "Restaurante", "Bar", "Playa", "Aventura"];
  
  const filteredPlaces = selectedPlaceCategory === "Todos" 
    ? mockTopPlaces 
    : mockTopPlaces.filter(place => place.category === selectedPlaceCategory);

  if (bookingLoading || propertyLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (bookingError || !booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Reservación no encontrada</h1>
          <p className="text-muted-foreground">La reservación que buscas no existe o ha sido eliminada.</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Propiedad no encontrada</h1>
          <p className="text-muted-foreground">No se pudo cargar la información de la propiedad.</p>
        </div>
      </div>
    );
  }

  const checkInDate = new Date(booking.check_in);
  const checkOutDate = new Date(booking.check_out);

  const formatDateRange = () => {
    const checkInFormatted = format(checkInDate, "d 'de' MMMM", { locale: es });
    const checkOutFormatted = format(checkOutDate, "d 'de' MMMM", { locale: es });
    return `${checkInFormatted} al ${checkOutFormatted}`;
  };

  const handleOpenLocation = () => {
    if (property.latitude && property.longitude) {
      window.open(`https://www.google.com/maps?q=${property.latitude},${property.longitude}`, '_blank');
    } else {
      toast.error('Ubicación no disponible');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Common container for hero and content alignment */}
      <div className="max-w-2xl mx-auto">
        {/* Hero Section */}
        <div className="relative h-[35vh] bg-muted overflow-hidden">
        {property.images && property.images.length > 0 ? (
          <img 
            src={property.images[0]} 
            alt={property.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-muted flex items-center justify-center">
            <Home className="h-16 w-16 text-muted-foreground/30" />
          </div>
        )}
      </div>

        {/* Content Container */}
        <div className="px-5 py-8 space-y-8">
        
        {/* Welcome Section */}
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-foreground">
            ¡Hola {booking.guest_name.split(' ')[0]}!
          </h2>
          <p className="text-muted-foreground">
            Esperamos que disfrutes tu estancia
          </p>
        </div>

        {/* Booking Information Card */}
        <div className="space-y-3 py-5 border-y border-border">
          <div className="flex items-start gap-3">
            <Home className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase text-muted-foreground font-medium mb-1">Propiedad</p>
              <p className="text-base font-medium text-foreground">{property.name || property.title}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase text-muted-foreground font-medium mb-1">Fechas</p>
              <p className="text-base font-medium text-foreground">{formatDateRange()}</p>
              <p className="text-sm text-muted-foreground">{booking.nights} {booking.nights === 1 ? 'noche' : 'noches'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase text-muted-foreground font-medium mb-1">Huéspedes</p>
              <p className="text-base font-medium text-foreground">
                {booking.total_guests} {booking.total_guests === 1 ? 'huésped' : 'huéspedes'}
              </p>
            </div>
          </div>

          <Button 
            onClick={handleOpenLocation}
            variant="outline"
            className="w-full mt-2"
          >
            <MapPin className="h-4 w-4" />
            Ver ubicación
          </Button>
        </div>

          {/* Entry Instructions Section */}
          {(entryInstructions.description || entryInstructions.code || entryInstructions.videoUrl) && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Key className="h-5 w-5 text-foreground" />
                <h3 className="text-xl font-bold text-foreground">Instrucciones de ingreso</h3>
              </div>
              
              {entryInstructions.description && (
                <p className="text-sm text-muted-foreground">
                  {entryInstructions.description}
                  {entryInstructions.videoUrl && (
                    <>
                      {". "}
                      <a 
                        href={entryInstructions.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors"
                      >
                        Ver video
                      </a>
                    </>
                  )}
                </p>
              )}

              {entryInstructions.code && (
                <div className="p-4 bg-muted/30 rounded-xl border border-border">
                  <p className="text-xs uppercase text-muted-foreground font-medium mb-2">Código</p>
                  <p className="text-2xl font-bold text-foreground tracking-wider">{entryInstructions.code}</p>
                </div>
              )}
            </div>
          )}

          {/* WiFi Section */}
          {(wifiCredentials.network || wifiCredentials.password) && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-foreground" />
                <h3 className="text-xl font-bold text-foreground">WiFi</h3>
              </div>
            
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-xl border border-border">
                {wifiCredentials.network && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground font-medium mb-2">Red WiFi</p>
                    <p className="text-base font-medium text-foreground">{wifiCredentials.network}</p>
                  </div>
                )}
                {wifiCredentials.password && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground font-medium mb-2">Contraseña</p>
                    <p className="text-base font-medium text-foreground">{wifiCredentials.password}</p>
                  </div>
                )}
              </div>

              {wifiCredentials.password && (
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(wifiCredentials.password);
                    toast.success('Contraseña copiada al portapapeles');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Copy className="h-4 w-4" />
                  Copiar contraseña
                </Button>
              )}
            </div>
          )}

        {/* Top Places Section */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-foreground">Lugares Top cerca de ti</h3>
          
          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {placeCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedPlaceCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedPlaceCategory === category
                    ? "bg-foreground text-background"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          
          {/* Places Grid */}
          <div className="grid grid-cols-2 gap-4">
            {filteredPlaces.map((place, index) => (
              <button key={index} className="group text-left">
                <div className="overflow-hidden rounded-xl mb-2">
                  <img 
                    src={place.image} 
                    alt={place.name}
                    className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <p className="text-base font-medium text-foreground">{place.name}</p>
                <p className="text-sm text-muted-foreground">{place.category}</p>
              </button>
            ))}
          </div>
          
          {/* Ver más Button */}
          <button className="w-full py-3 text-sm font-medium text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors">
            Ver más
          </button>
        </div>


        {/* House Rules Section */}
        <div className="space-y-4 pb-8">
          <h3 className="text-xl font-bold text-foreground">Reglas de la casa</h3>
          
          <div className="space-y-2">
            {propertyRules.length > 0 ? (
              propertyRules.map((rule) => (
                <div 
                  key={rule.id} 
                  className="flex items-center py-2 border-b border-border last:border-b-0"
                >
                  <p className="text-sm text-foreground">{rule.title}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No hay reglas especificadas para esta propiedad.</p>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default BookingDetail;
